import React, { useRef, useState } from "react";
import { useParams, useLocation, useSearchParams } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import Products from "./Products";
import FilterDrawer from "./FilterDrawer";

const CategoryPage = ({ userCountry, isAllProducts, isSearch }) => {
  const { category: urlCategory } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("q");

  const category = isAllProducts ? "all-products" : urlCategory;
  const hoodieRef = useRef(null);
  const jewelleryRef = useRef(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("sort");

  const getCategoryStatus = (slug) => {
    if (!slug) return null;
    const s = slug.toLowerCase();
    if (s === 'sweatshirts' || s === 'hoodies') return 'Coming in Winter';
    const soon = ['shorts', 'bags', 'caps', 'accessories', 'edits', 'all-products', 'fall-essentials', 'matching-sets', 'tonal-classics'];
    if (soon.includes(s) && s !== 'all-products') return 'Coming Soon'; // all-products might have some items, but user said tag it in ShopDropdown. 
    return null;
  };

  const status = getCategoryStatus(category) || (isSearch && getCategoryStatus(searchQuery));

  let categoryLabel = "Products";
  let subtitle = null;

  if (isSearch && searchQuery) {
    categoryLabel = `'${searchQuery}'`;
    subtitle = status ? "Showing 0 Results" : "Showing 30 Results";
  } else if (location.state?.customHeading) {
    categoryLabel = location.state.customHeading;
  } else if (category === "all-products" || category === "edits") {
    categoryLabel = "All Products";
  } else if (category === "ready-to-wear") {
    categoryLabel = "Ready To Wear";
  } else if (category === "t-shirts & tops") {
    categoryLabel = "T-Shirts & Tops";
  } else if (category) {
    categoryLabel = category.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  }

  return (
    <Box sx={{ pt: { xs: "170px", md: "185px" }, minHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
      {/* Category Heading */}
      <Typography
        variant="h3"
        sx={{
          fontFamily: '"Playfair Display", serif',
          fontWeight: 400,
          fontSize: { xs: "42px", md: "62px" },
          textAlign: "center",
          pt: 4,
          pb: (subtitle || status) ? 2 : 5,
          color: "#000",
          letterSpacing: "0.05rem",
        }}
      >
        {categoryLabel}
      </Typography>

      {subtitle && !status && (
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontSize: { xs: "12px", md: "14px" },
            textAlign: "center",
            pb: 5,
            color: "#555",
          }}
        >
          {subtitle}
        </Typography>
      )}

      {status ? (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10 }}>
          <Typography
            sx={{
              fontSize: '18px',
              letterSpacing: '2px',
              color: '#888',
              textTransform: 'uppercase',
              fontWeight: 300,
              mb: 2
            }}
          >
            {status}
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              color: '#aaa',
              maxWidth: '300px',
              textAlign: 'center',
              lineHeight: 1.6
            }}
          >
            We are currently preparing this collection for you. Please check back later or subscribe to our newsletter for updates.
          </Typography>
          <Button
            onClick={() => window.history.back()}
            sx={{
              mt: 4,
              color: '#000',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '1px',
              textDecoration: 'underline',
              '&:hover': { textDecoration: 'none', backgroundColor: 'transparent' }
            }}
          >
            BACK TO PREVIOUS
          </Button>
        </Box>
      ) : (
        <>
          {/* Filter + Sort Bar */}
          <Box sx={{ position: "relative", zIndex: 10, borderTop: "1px solid #e0e0e0", borderBottom: "1px solid #e0e0e0" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1.5,
                px: { xs: 2, md: 4 },
              }}
            >
              <Button
                disableRipple
                onClick={() => { setDrawerMode("filter"); setFilterOpen(true); }}
                sx={{
                  fontSize: "13px",
                  letterSpacing: "1px",
                  color: "#000",
                  fontWeight: 400,
                  p: 0,
                  minWidth: 0,
                  textTransform: "none",
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                FILTER ▸
              </Button>

              <Button
                disableRipple
                onClick={() => { setDrawerMode("sort"); setFilterOpen(true); }}
                sx={{
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                  color: "#555",
                  fontWeight: 400,
                  p: 0,
                  minWidth: 0,
                  textTransform: "none",
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                SORT BY —{" "}
                <span style={{ fontWeight: 700, color: "#000", marginLeft: "4px" }}>FEATURED</span>
              </Button>
            </Box>
          </Box>

          {/* Products grid */}
          <Products
            hoodieRef={hoodieRef}
            jewelleryRef={jewelleryRef}
            userCountry={userCountry}
            showOnlyHoodies={category === "hoodies" || category === "sweatshirts"}
            showOnlyTshirts={category === "t-shirts & tops" || category === "ready-to-wear"}
            showAllProducts={category === "all-products" || category === "edits" || isAllProducts}
            fromCategory={true}
          />
        </>
      )}

      {/* Filter Drawer */}
      <FilterDrawer open={filterOpen} mode={drawerMode} onClose={() => setFilterOpen(false)} />
    </Box>
  );
};

export default CategoryPage;
